// Export/print actions for the family report — shared by the on-page Share
// sheet and the settings panel so both paths run identical code.
import { saveAs } from 'file-saver'
import { generatePdf } from './pdfExport'
import { transformDataForReport } from './reportUtils'
import { translations } from './translations'

// Report strings outside any hook context: the table is read directly, with
// English as the fallback for a language that has no `report` block.
const reportText = (language, key) =>
  translations[language]?.report?.[key] ?? translations.en.report[key]

const generateFileName = (familyData, language = 'en') => {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const year = now.getFullYear()
  const date = `${month}-${day}-${year}`

  let familyName = reportText(language, 'defaultFamily')
  if (familyData?.parentName1) {
    familyName = familyData.parentName1.split(' ').pop()
  }

  return `${familyName}-${reportText(language, 'fileNameSuffix')}-${date}.pdf`
}

export async function exportFamilyPdf(data, viewMode, language) {
  if (!data) {
    console.error('No data available to generate PDF.')
    return
  }
  const transformedData = transformDataForReport(data)
  if (!transformedData) {
    console.error('Data transformation failed.')
    return
  }
  const blob = await generatePdf(transformedData, viewMode, language)
  const fileName = generateFileName(data.familyData, language)

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
  const isSafari = /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor)

  if (isIOS && navigator.share) {
    // iOS Web Share API (iOS 12+) — native share menu
    try {
      const file = new File([blob], fileName, { type: 'application/pdf' })
      await navigator.share({
        title: reportText(language, 'shareTitle'),
        text: reportText(language, 'shareText'),
        files: [file],
      })
      return
    } catch (error) {
      console.log('Native share failed, trying fallback:', error)
    }
  }

  if (isIOS || isSafari) {
    // iOS without Web Share / Safari: open the PDF so the user can share/save
    const url = URL.createObjectURL(blob)
    const newWindow = window.open(url, '_blank')
    if (!newWindow) {
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  } else {
    saveAs(blob, fileName)
  }
}

// Where an inline PDF cannot be printed from a hidden iframe:
//  - Android Chrome has no inline PDF viewer: the iframe downloads or renders
//    blank and contentWindow.print() is a silent no-op.
//  - iOS/iPadOS Safari rasterises an embedded PDF to an image of the first
//    page, so printing the iframe yields a one-page preview.
//  - `navigator.pdfViewerEnabled === false` is the standards signal that no
//    browser can show the PDF inline (Chromium/Firefox expose it).
// iPadOS 13+ reports a desktop Safari UA, hence the touch-point check.
const inlinePdfPrintUnsupported = () => {
  const ua = navigator.userAgent
  const isIPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1
  return (
    /Android|iPhone|iPad|iPod/.test(ua) ||
    isIPadOS ||
    navigator.pdfViewerEnabled === false
  )
}

// Web Share with a file attachment (iOS 15+, Android Chrome 75+). Probed
// with an empty placeholder so the check can run before the PDF exists.
const canShareFiles = () => {
  try {
    const probe = new File([''], 'probe.pdf', { type: 'application/pdf' })
    return typeof navigator.share === 'function' && navigator.canShare?.({ files: [probe] }) === true
  } catch {
    return false
  }
}

export async function printFamilyPdf(data, viewMode, language) {
  if (!data) {
    console.error('No data available to print.')
    return
  }
  const transformedData = transformDataForReport(data)
  if (!transformedData) {
    console.error('Data transformation failed.')
    return
  }

  if (inlinePdfPrintUnsupported()) {
    // Mobile path: hand the PDF to the system viewer, which owns printing.
    // Prefer the share sheet (iOS/Android both list Print there). Otherwise
    // open the PDF full-page in a new tab. Safari only honours window.open
    // synchronously inside the user gesture, so the tab is opened *before*
    // the (async) PDF generation and pointed at the blob once it exists.
    const useShare = canShareFiles()
    const popup = useShare ? null : window.open('', '_blank')
    let blob
    try {
      blob = await generatePdf(transformedData, viewMode, language)
    } catch (error) {
      popup?.close()
      throw error
    }
    const fileName = generateFileName(data.familyData, language)

    if (useShare) {
      try {
        await navigator.share({
          title: reportText(language, 'shareTitle'),
          text: reportText(language, 'shareText'),
          files: [new File([blob], fileName, { type: 'application/pdf' })],
        })
        return
      } catch (error) {
        if (error?.name === 'AbortError') return // user dismissed the sheet
        console.log('Native share failed, falling back to download:', error)
        saveAs(blob, fileName)
        return
      }
    }

    if (popup && !popup.closed) {
      const url = URL.createObjectURL(blob)
      popup.location.href = url
      // The new tab needs the blob while it loads (and while the user prints
      // from it); revoke on a generous delay rather than immediately.
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } else {
      // Popup blocked: a download still lets the user print from the viewer.
      saveAs(blob, fileName)
    }
    return
  }

  // Desktop path: print the PDF from a hidden same-origin iframe.
  const blob = await generatePdf(transformedData, viewMode, language)
  const url = URL.createObjectURL(blob)
  const iframe = document.createElement('iframe')
  iframe.style.display = 'none'
  iframe.src = url
  document.body.appendChild(iframe)
  iframe.onload = () => {
    // Chrome 77+ ignores print() from an unfocused frame.
    iframe.contentWindow.focus()
    iframe.contentWindow.print()
    // print() is blocking in Chromium but not in Firefox, so clean up on a
    // delay long enough for the dialog rather than right after it returns.
    setTimeout(() => {
      URL.revokeObjectURL(url)
      iframe.remove()
    }, 60_000)
  }
}
